import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Search as SearchIcon, GitCompare, Store, ArrowRight, Check, Scale, BadgeCheck, FileText,
  Disc3, Filter, Cog, Car, Zap, Lightbulb, Snowflake, Wind, Settings2, Armchair,
} from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { SearchBar } from "@/components/SearchBar";

const CAT_ICONS = {
  brakes: Disc3, filters: Filter, engine: Cog, suspension: Car, electrical: Zap,
  lighting: Lightbulb, cooling: Snowflake, exhaust: Wind, transmission: Settings2, body: Armchair,
};

const HERO_IMG = "https://images.unsplash.com/photo-1429772011165-0c2e054367b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxjYXIlMjBlbmdpbmUlMjBwYXJ0cyUyMGNsZWFufGVufDB8fHx8MTc4NTg3MTc5OXww&ixlib=rb-4.1.0&q=85";
const PARTNER_IMG = "https://images.pexels.com/photos/3855473/pexels-photo-3855473.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function Section({ children, className = "" }) {
  return <section className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</section>;
}

export default function Home() {
  const { t } = useI18n();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const totalParts = categories.reduce((a, c) => a + (c.count || 0), 0);

  return (
    <div>
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-border bg-white">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: `url(${HERO_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute -right-40 -top-40 h-96 w-96 rounded-full bg-blue-100/60 blur-3xl" />
        <Section className="relative py-20 lg:py-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-primary">
              <ShieldCheck className="h-4 w-4" /> {t("hero.badge")}
            </span>
            <h1 className="mt-6 font-heading text-4xl font-bold leading-[1.05] tracking-tighter text-slate-900 sm:text-5xl lg:text-6xl">
              {t("hero.title")}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">{t("hero.subtitle")}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="mt-10 max-w-4xl">
            <SearchBar variant="hero" />
          </motion.div>
          <div className="mt-10 flex flex-wrap gap-x-12 gap-y-4">
            {[
              [`${categories.length ? "8" : "8"}+`, t("hero.stat.retailers")],
              [`${totalParts || "50"}+`, t("hero.stat.parts")],
              ["4", t("hero.stat.markets")],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="font-heading text-3xl font-bold tracking-tight text-slate-900">{n}</div>
                <div className="text-sm text-slate-500">{l}</div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* Categories */}
      <Section className="py-20">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">{t("cat.title")}</h2>
            <p className="mt-2 text-slate-600">{t("cat.subtitle")}</p>
          </div>
          <Link to="/search" data-testid="view-all-categories" className="hidden items-center gap-1 text-sm font-semibold text-primary hover:underline sm:flex">
            {t("cat.view")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {categories.map((c, i) => (
            <motion.div key={c.slug} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}>
              <Link
                to={`/search?category=${c.slug}`}
                data-testid={`category-${c.slug}`}
                className="group flex h-full flex-col justify-between rounded-lg border border-border bg-white p-5 transition-transform duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-md"
              >
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-blue-50 text-primary transition-colors duration-200 group-hover:bg-primary group-hover:text-white">
                  {React.createElement(CAT_ICONS[c.slug] || SearchIcon, { className: "h-5 w-5" })}
                </div>
                <div className="mt-6">
                  <div className="font-heading font-semibold tracking-tight text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-400">{c.count} {t("cat.count")}</div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* How it works */}
      <div className="border-y border-border bg-white">
        <Section className="py-20">
          <h2 className="max-w-2xl font-heading text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">{t("how.title")}</h2>
          <p className="mt-2 text-slate-600">{t("how.subtitle")}</p>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {[
              [SearchIcon, t("how.s1.title"), t("how.s1.text")],
              [GitCompare, t("how.s2.title"), t("how.s2.text")],
              [Store, t("how.s3.title"), t("how.s3.text")],
            ].map(([Icon, title, text], i) => (
              <div key={title} className="relative rounded-lg border border-border bg-[#F8F9FA] p-8">
                <span className="absolute right-6 top-6 font-heading text-5xl font-bold text-slate-100">{i + 1}</span>
                <div className="grid h-12 w-12 place-items-center rounded-lg bg-primary text-white"><Icon className="h-6 w-6" /></div>
                <h3 className="mt-6 font-heading text-xl font-semibold tracking-tight text-slate-900">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* Trust */}
      <Section className="py-20">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">{t("trust.title")}</h2>
            <p className="mt-3 text-slate-600">{t("trust.subtitle")}</p>
            <Link to="/methodology" data-testid="methodology-link" className="mt-6 inline-flex items-center gap-2 rounded-full border border-border bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 transition-colors duration-200 hover:border-primary hover:text-primary">
              <FileText className="h-4 w-4" /> {t("trust.method")}
            </Link>
          </div>
          <div className="grid gap-4">
            {[
              [Scale, t("trust.1.title"), t("trust.1.text")],
              [BadgeCheck, t("trust.2.title"), t("trust.2.text")],
              [FileText, t("trust.3.title"), t("trust.3.text")],
            ].map(([Icon, title, text]) => (
              <div key={title} className="flex gap-4 rounded-lg border border-border bg-white p-6">
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-emerald-50 text-emerald-600"><Icon className="h-5 w-5" /></div>
                <div>
                  <h3 className="font-heading font-semibold tracking-tight text-slate-900">{title}</h3>
                  <p className="mt-1 text-sm text-slate-600">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Partner CTA */}
      <Section className="pb-24">
        <div className="overflow-hidden rounded-2xl border border-border bg-slate-900">
          <div className="grid lg:grid-cols-2">
            <div className="p-10 lg:p-14">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400">{t("nav.partners")}</span>
              <h2 className="mt-4 font-heading text-2xl font-semibold tracking-tight text-white sm:text-3xl lg:text-4xl">{t("partner.title")}</h2>
              <p className="mt-3 max-w-md text-slate-300">{t("partner.subtitle")}</p>
              <ul className="mt-6 space-y-3">
                {[t("partner.b1"), t("partner.b2"), t("partner.b3")].map((b) => (
                  <li key={b} className="flex items-center gap-3 text-sm text-slate-200">
                    <Check className="h-5 w-5 text-emerald-400" /> {b}
                  </li>
                ))}
              </ul>
              <Link to="/partner" data-testid="become-partner-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 font-heading text-base font-semibold text-slate-900 transition-colors duration-200 hover:bg-blue-50">
                {t("partner.cta")} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="relative min-h-[280px]">
              <img src={PARTNER_IMG} alt="Mechanic partner" className="absolute inset-0 h-full w-full object-cover" />
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
